/**
 * The sentence a failed request shows the user.
 *
 * The API words its own errors, and that wording wins. But an error can arrive
 * from somewhere that does not: an older API build that printed the raw
 * exception (`PreparedStatementCallback; SQL [INSERT INTO classes ...]`), nginx
 * answering 502 with an HTML page, or no response at all. None of those should
 * reach the screen as-is, so anything that looks like internals is replaced
 * with a plain sentence for the status.
 */

/**
 * SQL, stack frames, Java class names: never meant for the person at the screen.
 * Case-sensitive on purpose. Raw SQL is upper case, and a sentence such as
 * "Select a stream from the list" must not be mistaken for it.
 */
const TECHNICAL = [
  /PreparedStatementCallback|SQL \[|\bINSERT INTO\b|\bDELETE FROM\b|\bSELECT\b[\s\S]*\bFROM\b|\bUPDATE\b\s+\w+\s+SET\b|\bERROR:\s/,
  /violates [\w -]*constraint|duplicate key value/,
  /\w+(?:Exception|Error)\b:?|\bat [\w$.]+\([\w.]*:\d+\)|\b(?:org|java|javax|jakarta|com)\.[a-z]+\.[\w.]+/,
  /<\/?html|<!doctype/i,
]

export function looksTechnical(message: string): boolean {
  return TECHNICAL.some((re) => re.test(message))
}

/** A sentence for when the server gave none we can use. */
export function messageForStatus(status: number, axiosCode?: string): string {
  if (axiosCode === 'ECONNABORTED' || axiosCode === 'ETIMEDOUT') {
    return 'The request took too long. Check your connection and try again.'
  }
  if (status === 0) return "We couldn't reach the server. Check your connection and try again."
  if (status === 400 || status === 422) {
    return "Some of the information isn't valid. Check the form and try again."
  }
  if (status === 401) return 'Your session has ended. Please sign in again.'
  if (status === 403) return "You don't have permission to do that."
  if (status === 404) return "We couldn't find what you were looking for. It may have been moved or deleted."
  if (status === 409) return 'This conflicts with existing information. Refresh the page and try again.'
  if (status === 413) return 'The file is too large to upload.'
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.'
  if (status === 502 || status === 503 || status === 504) {
    return 'The system is busy or being updated. Please try again in a moment.'
  }
  if (status >= 500) return 'Something went wrong on our side. Please try again.'
  return "The request couldn't be completed. Please try again."
}

/**
 * The server's message when it is fit to show, otherwise one for the status.
 * A raw duplicate-key error still says what happened, without the SQL.
 */
export function friendlyMessage(status: number, serverMessage: unknown, axiosCode?: string): string {
  if (typeof serverMessage === 'string') {
    const m = serverMessage.trim()
    if (m && !looksTechnical(m)) return m
    if (/duplicate key value/i.test(m)) {
      return 'This already exists. Use a different value and try again.'
    }
    if (/violates foreign key constraint/i.test(m) && /still referenced/i.test(m)) {
      return "This can't be removed because other records still use it."
    }
  }
  return messageForStatus(status, axiosCode)
}
